import { createFileRoute } from "@tanstack/react-router";
import { Clock, ChevronRight } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BLOG_POSTS } from "@/lib/blog-data";
import { buildSeoMeta } from "@/lib/seo";

export const Route = createFileRoute("/blog/")({
  head: () =>
    buildSeoMeta({
      title: "Blog SyncTech — wdrożenia Baselinker, integracje, automatyzacje e-commerce",
      description:
        "Praktyczne poradniki o Baselinker, integracjach Shopify, ShopGold, SellAsist, automatyzacji zamówień i marketingu e-commerce. Bez ogólników — tylko konkrety.",
      path: "/blog",
    }),
  component: BlogIndex,
});

function BlogIndex() {
  const posts = [...BLOG_POSTS].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-32">
        <header className="mb-12 max-w-3xl">
          <span className="inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            Blog
          </span>
          <h1 className="mt-4 font-display text-4xl font-bold md:text-5xl">
            Praktyczna wiedza o <span className="text-gradient">e-commerce, Baselinker i integracjach</span>
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Konkretne poradniki, case studies i porównania. Bez wody, bez ogólników — to, co realnie działa w polskim e-commerce.
          </p>
        </header>

        {/* Featured */}
        <a
          href={`/blog/${featured.slug}`}
          className="group mb-12 block overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-card to-primary-glow/5 p-8 transition hover:-translate-y-1 hover:border-primary hover:shadow-elegant md:p-12"
        >
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">
            ⭐ Najnowszy artykuł · {featured.category}
          </span>
          <h2 className="mt-3 font-display text-2xl font-bold leading-tight md:text-4xl">
            {featured.title}
          </h2>
          <p className="mt-4 max-w-3xl text-muted-foreground">{featured.excerpt}</p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary">
            Czytaj artykuł <ChevronRight className="h-4 w-4 transition group-hover:translate-x-1" />
          </div>
        </a>

        {/* Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p) => (
            <a
              key={p.slug}
              href={`/blog/${p.slug}`}
              className="group flex flex-col rounded-2xl border border-border bg-card p-6 transition hover:-translate-y-1 hover:border-primary hover:shadow-elegant"
            >
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                {p.category}
              </span>
              <h3 className="mt-2 font-display text-lg font-bold leading-tight group-hover:text-primary">
                {p.title}
              </h3>
              <p className="mt-3 line-clamp-3 flex-1 text-sm text-muted-foreground">{p.excerpt}</p>
              <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {p.readingTime} min
                </span>
                <span>·</span>
                <span>
                  {new Date(p.publishedAt).toLocaleDateString("pl-PL", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </div>
            </a>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
